import { z } from 'zod';

const Text = z.string().min(1);
const Age = z.enum(['3-4', '5-6', '7-8']);
export const LessonSource = z.strictObject({
  id: Text, url: z.url(), title: Text, retrieved_on: z.iso.date(), notes: Text,
});
export const LessonFact = z.strictObject({ id: Text, text: Text, source_ids: z.array(Text).min(1) });
export const LessonCard = z.strictObject({
  id: z.string().regex(/^[a-z]+\.[a-z0-9-]+$/), revision: z.int().min(1),
  subject: z.enum(['Math', 'Physics', 'Science', 'Language', 'Nature', 'Geography', 'History', 'Art', 'Music']),
  age_bands: z.array(Age).min(1), objective: Text, success_criterion: Text,
  prerequisites: z.array(z.string()), facts: z.array(LessonFact).min(1), sources: z.array(LessonSource).min(1),
  misconceptions: z.array(z.string()), activities_by_band: z.partialRecord(Age, Text),
  capabilities: z.array(z.string()).min(1), object_roles: z.array(z.string()),
  application: Text, safety: z.array(z.string()).min(1),
  review_state: z.enum(['source_grounded', 'educator_reviewed']),
  educator_review: z.strictObject({ reviewer: Text, reviewed_on: z.iso.date(), record: Text }).nullable().optional(),
  match_terms: z.array(z.string()).min(1), unsupported_terms: z.array(z.string()).optional(),
}).superRefine((card, ctx) => {
  if ((card.review_state === 'educator_reviewed') !== Boolean(card.educator_review))
    ctx.addIssue({ code: 'custom', message: 'Educator review requires a real review record' });
  if (card.age_bands.some((band) => !card.activities_by_band[band]) || Object.keys(card.activities_by_band).some((band) => !card.age_bands.includes(band as z.infer<typeof Age>)))
    ctx.addIssue({ code: 'custom', message: 'Every age band needs its own explicit activity' });
  for (const ids of [card.age_bands, card.facts.map((f) => f.id), card.sources.map((s) => s.id)])
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: 'custom', message: 'Duplicate lesson identities' });
  if (card.facts.some((f) => f.source_ids.some((id) => !card.sources.some((s) => s.id === id))))
    ctx.addIssue({ code: 'custom', message: 'Fact references an absent source' });
}).meta({ id: 'LessonCard' });
export type LessonCard = z.infer<typeof LessonCard>;
